import { useEffect, useState } from 'react';
import postRobot from 'post-robot';

import { optimizeWaypointsWithSuggestion } from '../api/services/Rides';
import { useToastStore } from '../shared/stores/toastStore';

export type RideSuggestion = {
  waypointsAttributes: WaypointsForSmartPooling[];
  riders: RiderForSmartPooling[];
};

interface useSmartPoolingSuggestionHook {
  shouldUseSmartpooling: boolean;
  suggestedRideDetails?: RideDetails;
  suggestedRouteDetails?: SuggestedRouteDetails;
  suggestionData?: RideSuggestion;
}

export const useSmartPoolingSuggestion = (rideDetails?: RideDetails): useSmartPoolingSuggestionHook => {
  const { addToast } = useToastStore();
  const [suggestedRideDetails, setSuggestedRideDetails] = useState<RideDetails>();
  const [suggestedRouteDetails, setSuggestedRouteDetails] = useState<SuggestedRouteDetails>();
  const [suggestionData, setSuggestionData] = useState<RideSuggestion>();
  const isEditView = window.location.pathname.match(/edit/);

  const originalRiderSlugs =
    rideDetails?.waypoints.flatMap((waypoint) => waypoint.riders.map((rider) => rider.slug)) || [];

  // the suggestedRideDetails might not introduce new waypoints, only riders, so we need to check if the number of waypoints is the same or there is a suggestedRouteDetails
  const shouldReturnSuggestedRideDetails =
    suggestedRideDetails?.waypoints.length === rideDetails?.waypoints.length || suggestedRouteDetails !== undefined;

  const addSuggestedDataToRideDetails = async (suggestionDetails: RideSuggestion) => {
    const smartPoolingWaypoint = suggestionDetails?.waypointsAttributes.find((waypoint) => !waypoint.anchor);
    const smartPoolingRiders = suggestionDetails?.riders.filter((rider) => originalRiderSlugs.indexOf(rider.id) === -1);

    if (smartPoolingWaypoint && rideDetails && suggestionDetails?.riders.length) {
      const existingWaypointIndex = rideDetails.waypoints.findIndex(
        (waypoint) => !waypoint.anchor && waypoint.location.placeId === smartPoolingWaypoint.location.placeId
      );

      if (existingWaypointIndex > -1) {
        setSuggestedRideDetails({
          ...rideDetails,
          waypoints: rideDetails.waypoints.map((waypoint, index) => {
            if (index === existingWaypointIndex) {
              return {
                ...rideDetails.waypoints[existingWaypointIndex],
                riders: [
                  ...rideDetails.waypoints[existingWaypointIndex].riders,
                  ...smartPoolingRiders.map((rider) => ({
                    ...rider,
                    fullName: rider.name,
                    slug: rider.id,
                  })),
                ],
              };
            }

            return waypoint;
          }),
        });
      } else {
        const waypointWithSuggestion = [
          ...rideDetails.waypoints,
          {
            ...smartPoolingWaypoint,
            anchor: false,
            location: {
              ...smartPoolingWaypoint.location,
              lat: parseFloat(smartPoolingWaypoint.latLng.split(',')[0]),
              lng: parseFloat(smartPoolingWaypoint.latLng.split(',')[1]),
            },
            riders: smartPoolingRiders.map((rider) => ({
              ...rider,
              fullName: rider.name,
              slug: rider.id,
            })),
          },
        ];

        const optimizationResponse = await optimizeWaypointsWithSuggestion({
          tripSlug: rideDetails.slug,
          timeAnchor: suggestionDetails.waypointsAttributes[0].anchor === true ? 'pick_up' : 'drop_off',
          waypointsAttributes: waypointWithSuggestion.map(({ slug, passengerIds, location, anchor }) => ({
            slug,
            passengerIds,
            location,
            anchor,
          })),
        });

        if (optimizationResponse.type === 'success') {
          setSuggestedRideDetails({
            ...rideDetails,
            waypoints: optimizationResponse.data.waypoints?.map((waypoint) => {
              return waypointWithSuggestion.find(
                (updatedWaypoint) => updatedWaypoint.location.placeId === waypoint.location.placeId
              );
            }),
          });
          setSuggestedRouteDetails({
            overviewPolyline: optimizationResponse.data.overviewPolyline,
            legs: optimizationResponse.data.legs,
          });
        } else {
          optimizationResponse.error.errors?.forEach((error) => addToast({ severity: 'error', message: error }));
        }
      }
      addToast({
        severity: 'success',
        message:
          'Rider added successfully! Make sure the waypoints are in the right order so you get the optimal route.',
      });
    }
  };

  const getSmartPoolingData = async () => {
    try {
      const { data } = await postRobot.send(window.opener, 'getSmartPoolingData');

      setSuggestionData(data);
      addSuggestedDataToRideDetails(data);
    } catch (e) {
      delete window.opener;
    }
  };

  useEffect(() => {
    !suggestedRideDetails && window.opener && rideDetails && getSmartPoolingData();
  }, [rideDetails]);

  return {
    shouldUseSmartpooling: !!window.opener && !!isEditView,
    suggestedRideDetails: shouldReturnSuggestedRideDetails ? suggestedRideDetails : undefined,
    suggestedRouteDetails,
    suggestionData,
  };
};
